import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import { ManualTreeExcerptColorResult, ManualTreeSection, User } from '../../../../../api/model';
import useContextMenu from '../../../../../hooks/useContextMenu';
import ConfirmModal from '../../../../modules/ConfirmModal';
import { SectionTreeMenuCopy } from './sectionmenu/SectionTreeMenuCopy';
import { SectionTreeMenuDelete } from './sectionmenu/SectionTreeMenuDelete';
import { SectionTreeMenuEdit } from './sectionmenu/SectionTreeMenuEdit';
import { SectionTreeMenuMove } from './sectionmenu/SectionTreeMenuMove';
import { SectionTreeMenuPdfPreview } from './sectionmenu/SectionTreeMenuPdfPreview';
import { SectionSlimMenu } from './slimmenu/SectionSlimMenu';

type TreeProps = {
  /* eslint @typescript-eslint/no-explicit-any: off */
  /* eslint @typescript-eslint/no-unsafe-call: off */
  productionId: number;
  productionDivisionId: number;
  selectedSectionId: number | null;
  tree: ManualTreeSection;
  user: User;
  isEdit: boolean;
  setIsEdit: (isEdit: boolean) => void;
  setSectionId: (sectionId: number) => void;
  onSearch: () => void;
  beforeMovingMyEditSection: boolean | null;
  setBeforeMovingMyEditSection: (beforeMovingMyEditSection: boolean | null) => void;
  allOpen: boolean;
  allClose: boolean;
  TreeExcerptColor: ManualTreeExcerptColorResult[];
  excerptcolorRefetch: () => void;
  setSecReading: (secReading: boolean) => void;
  secReading: boolean;
};
function SectionTree({
  productionId,
  productionDivisionId,
  selectedSectionId,
  tree,
  user,
  isEdit,
  setIsEdit,
  setSectionId,
  onSearch,
  beforeMovingMyEditSection,
  setBeforeMovingMyEditSection,
  allOpen,
  allClose,
  TreeExcerptColor,
  excerptcolorRefetch,
  setSecReading,
  secReading,
}: TreeProps) {
  const { t } = useTranslation();
  const level = String(tree.level) || '2';
  const [contextOpen, setContextOpen] = useState(false);
  const [confirmSelectSection, setConfirmSelectSection] = useState(false);
  const { points, setPoints } = useContextMenu();
  const ref = useRef(null);
  useClickAway(ref, () => {
    setContextOpen(false);
  });

  /**
   * ブラウザのwindowサイズ取得
   */
  const [contextUp, setContextUp] = useState(false);
  const useWindowSize = (): number[] => {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
      const updateSize = (): void => {
        setSize([window.innerWidth, window.innerHeight]);
      };

      window.addEventListener('resize', updateSize);
      updateSize();

      return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
  };
  const [width, height] = useWindowSize();

  const handleContextMenu = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.preventDefault();
    setContextOpen(true);
  };
  const sectionId: number = useMemo(() => tree.section_id || 0, [tree.section_id]);
  const isEnable = useMemo(() => tree.enabled === 1, [tree.enabled]);
  const [isOpen, setIsOpen] = useState(false); // 直下を開くか

  // 手動でツリーを開閉
  const handleOpen = () => {
    setIsOpen(!isOpen);
  };

  // 読込み時の開閉フラグ
  useEffect(() => {
    if (allClose) {
      setIsOpen(false); // 全閉じ
    } else if (allOpen) {
      setIsOpen(true); // 全開き
    } else {
      setIsOpen(tree.is_open === true); // apiからの情報
    }
  }, [tree, allOpen, allClose]);

  // タイトルの色
  const titleColor = useMemo(() => {
    if (tree.level === 2) {
      if (!isEnable) {
        return 'lightgrey';
      }
      if (tree.edit_lock_user_id != null) {
        if (tree.edit_lock_user_id === user.user_id) {
          return 'blue'; // 自身が編集中
        }
        return 'steelblue'; // 他者が編集中
      }
      return '';
    }
    return !isEnable ? 'lightgrey' : '';
  }, [isEnable, tree, user]);

  const xrefId = tree.xref_id;
  const handleSectionClick = () => {
    // コンテキストメニューが開いている場合、L2セクションを移動しない
    if (!contextOpen) {
      if (selectedSectionId !== sectionId && beforeMovingMyEditSection) {
        // L2セクションが変わる かつ 移動前のL2セクションが編集中なら 移動前に確認ダイアログを表示する
        setConfirmSelectSection(true);
      } else {
        if (selectedSectionId !== sectionId) {
          // セクションが変わる場合
          setSecReading(true);
        }
        setSectionId(sectionId);
      }
    }
  };

  // コンポーネントの初期化時にのみサービスを呼び出したい
  useEffect(() => {
    if (tree.level === 2 && selectedSectionId === tree.section_id) {
      // L2で選択セクションとツリーが一致する場合、そのツリーが自身が編集者になっているかのフラグを更新
      setBeforeMovingMyEditSection(tree.edit_lock_user_id !== null && tree.edit_lock_user_id === user.user_id);
    }
  }, [setBeforeMovingMyEditSection, tree, user, selectedSectionId]);

  // WebOMプレビューメニューの表示／非表示(navi, 翻訳navi 以外でWebOmプレビューを表示)
  const isShowPreviewWebOm = useMemo(
    () => [1, 2, 4, 5, 6, 7, 8, 10, 11, 12, 13, 14].includes(productionDivisionId),
    [productionDivisionId]
  );

  return (
    <li className={`tree${level}`} key={`tree-${level}-${String(tree.chapter_id)}-${String(tree.section_id)}`}>
      <i
        className={`fa-solid ${isOpen ? 'fa-caret-down' : 'fa-caret-right'}`}
        style={{ color: TreeExcerptColor.find((item) => item.section_id === Number(tree.section_id))?.color }}
        onClick={() => handleOpen()}
      />
      <span
        style={{
          cursor: tree.level === 2 ? 'pointer' : '',
          backgroundColor: selectedSectionId === sectionId ? '#ffeedd' : '',
        }}
        onClick={() => tree.level === 2 && handleSectionClick()}
        onContextMenu={(event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
          /* 検索にヒットしなかったものはコンテキストメニューが表示できない */
          if (isEnable) {
            event.preventDefault();
            if (height - event.clientY < 220) {
              setContextUp(true);
              setPoints({ x: event.clientX, y: height - event.clientY + 5 });
            } else {
              setPoints({ x: event.clientX, y: event.clientY + 5 });
            }
            handleContextMenu(event);
          }
        }}
      >
        <span style={{ color: '#c21413', fontWeight: 'bold', margin: '0px 4px 0px 0px' }}>{tree.level}</span>
        {tree.level === 2 && tree.edit_lock_user_id != null ? (
          <>
            {/* L2編集ロック */}
            <span style={{ color: titleColor }}>{tree.title}</span>
            <span style={{ color: '#808080', margin: '0px 0px 0px 10px' }}>{tree.edit_lock_user}</span>
          </>
        ) : (
          <>
            {/* L2編集ロック以外 */}
            <span style={{ color: titleColor }}>{tree.title}</span>
          </>
        )}
        <ul
          className="mt-popup"
          style={{
            display: contextOpen ? 'block' : '',
            top: contextUp ? '' : `${points.y}px`,
            bottom: contextUp ? `${points.y}px` : '',
            left: `${points.x}px`,
          }}
          ref={ref}
        >
          {tree.level === 2 && tree.section_id && contextOpen && !secReading && (
            <SectionTreeMenuEdit
              sectionId={tree.section_id}
              isLock={tree.edit_lock_user_id !== null}
              isOwnLock={tree.edit_lock_user_id !== null && tree.edit_lock_user_id === user.user_id}
              isEdit={isEdit}
              setIsEdit={setIsEdit}
              setSectionId={setSectionId}
              setContextOpen={setContextOpen}
              onSearch={onSearch}
              setSecReading={setSecReading}
            />
          )}
          {tree.level !== 2 && <SectionTreeMenuCopy xrefId={xrefId} line={tree.level === 2} />}
          {tree.level !== 5 && (
            // L2～L4まで表示
            <SectionSlimMenu
              productionId={productionId}
              chapterId={tree.chapter_id}
              sectionId={tree.section_id}
              level={tree.level}
              title={tree.title}
              excerptcolorRefetch={excerptcolorRefetch}
            />
          )}
          {tree.level === 2 && (
            <SectionTreeMenuPdfPreview
              chapterId={undefined}
              sectionId={sectionId}
              isShowPreviewWebOm={isShowPreviewWebOm}
            />
          )}
          {tree.level === 2 && tree.section_id && (
            <>
              <SectionTreeMenuMove sectionId={sectionId} productionId={productionId} />
              <SectionTreeMenuDelete sectionId={sectionId} productionId={productionId} />
            </>
          )}
        </ul>
      </span>
      {tree.manual_tree_sections && tree.manual_tree_sections?.length > 0 && (
        <ul className={isOpen ? `tree${level}` : `treeul${level}`}>
          {tree.manual_tree_sections?.map((treeSection: ManualTreeSection) => (
            <SectionTree
              isEdit={isEdit}
              setIsEdit={setIsEdit}
              productionId={productionId}
              productionDivisionId={productionDivisionId}
              selectedSectionId={selectedSectionId}
              user={user}
              tree={treeSection}
              setSectionId={setSectionId}
              onSearch={onSearch}
              key={`treeSection-${String(treeSection.chapter_id)}-${String(treeSection.section_id)}-${String(
                treeSection.level
              )}-${String(treeSection.l2_section_id)}-${String(treeSection.l3_section_id)}`}
              beforeMovingMyEditSection={beforeMovingMyEditSection}
              setBeforeMovingMyEditSection={setBeforeMovingMyEditSection}
              allOpen={allOpen}
              allClose={allClose}
              TreeExcerptColor={TreeExcerptColor}
              excerptcolorRefetch={excerptcolorRefetch}
              setSecReading={setSecReading}
              secReading={secReading}
            />
          ))}
        </ul>
      )}
      {confirmSelectSection && (
        <ConfirmModal
          onClickCancel={() => setConfirmSelectSection(false)}
          onClickOk={() => {
            setConfirmSelectSection(false);
            setSecReading(true);
            setSectionId(sectionId);
          }}
        >
          {t('編集中ですが開きますか？')}
        </ConfirmModal>
      )}
    </li>
  );
}
export { SectionTree };
